// Auth service for session management and redirect logic

const APP_URL = 'https://app.nisa.coach'

interface BetterAuthSession {
  session: {
    id: string
    userId: string
    expiresAt: string
    [key: string]: any
  } | null
  user: any | null
}

/**
 * Check if user has an active session on the main app
 * Uses Better Auth's standard /api/auth/get-session endpoint
 */
export const checkSession = async (): Promise<{ authenticated: boolean }> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 3000) // Increased to 3 seconds

  try {
    console.log(
      '[Auth] Checking session at:',
      `${APP_URL}/api/auth/get-session`,
    )
    console.log('[Auth] Request started at:', new Date().toISOString())

    const response = await fetch(`${APP_URL}/api/auth/get-session`, {
      method: 'GET',
      credentials: 'include', // Include cookies for cross-subdomain auth
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    console.log('[Auth] Response received at:', new Date().toISOString())
    console.log('[Auth] Session check response status:', response.status)

    if (!response.ok) {
      console.log('[Auth] Session check failed with status:', response.status)
      // Don't try to read the response body — it may be empty or malformed on 502/503
      return { authenticated: false }
    }

    let data: BetterAuthSession
    try {
      data = await response.json()
    } catch (parseError) {
      console.log('[Auth] Failed to parse session response JSON:', parseError)
      return { authenticated: false }
    }

    console.log('[Auth] Session data received:', {
      hasSession: !!data.session,
      hasUser: !!data.user,
      sessionId: data.session?.id,
      userId: data.user?.id,
    })

    // Better Auth returns { session: {...}, user: {...} } when authenticated
    // or { session: null, user: null } when not authenticated
    const isAuthenticated = !!data.session && !!data.user

    console.log('[Auth] Authentication result:', isAuthenticated)

    return { authenticated: isAuthenticated }
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.log(
          '[Auth] Session check timed out after 3s - continuing with marketing site',
        )
      } else {
        console.log('[Auth] Session check error:', error.message)
      }
    }

    // Fail open - if session check fails, don't block the marketing site
    return { authenticated: false }
  }
}

/**
 * Redirect to main app dashboard
 */
export const redirectToApp = (): void => {
  console.log('[Auth] Redirecting to app dashboard')
  window.location.href = `${APP_URL}`
}
